import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Button, IconButton, SvgIcon, Tooltip } from "@mui/material";

import "./style.scss";
import { ReactComponent as CloseIcon } from "../../../assets/cross.icon.svg";
import { ReactComponent as CopyIcon } from "../../../assets/copy.icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.icon.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/download.icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit.icon.svg";
import { ReactComponent as PlusIcon } from "../../../assets/plus.icon.svg";
import { ReactComponent as SearchIcon } from "../../../assets/search.icon.svg";
import {
    createChatBot,
    deleteChatBot,
    downloadManifest,
    fetchChatBots,
    updateChatBot
} from "../../../api/admin/chat-bots/endpoints-referrers";
import {
    ChatBot,
    ChatPlatform,
    CreateChatBotRequest,
    UpdateChatBotRequest
} from "../../../common/interfaces/admin/chat-bot.interface";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import { SelectionFilter } from "../../../components/menu-filter/menu-filter.comp";
import Toast, { ToastContainer } from "../../../components/toast/toast.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import CreateChatBotModal from "./create-chat-bot-modal.comp";
import EditChatBotModal from "./edit-chat-bot-modal.comp";

const platformLabel: Record<string, string> = {
    [ChatPlatform.Cliq]: "Zoho Cliq",
    [ChatPlatform.Teams]: "Microsoft Teams",
    [ChatPlatform.Slack]: "Slack",
    [ChatPlatform.GoogleChat]: "Google Chat"
};

const sortOptions = ["Name", "Platform", "Status"];

// Enter/Space activation for the copyable Bot ID / Webhook rows, which render as
// role="button" <div>s rather than native buttons (WCAG 2.1.1). Delegates to the
// element's own onClick so the copy behaviour stays in one place.
const activateOnKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
    }
};

const getStatusInfo = (bot: ChatBot): { label: string; color: string } => {
    if (!bot.isActive) return { label: "Inactive", color: "default" };
    if (!bot.hasCredentials) return { label: "Missing credentials", color: "orange" };
    return { label: "Active", color: "green" };
};

const ChatIntegrationsView = () => {
    const { user } = FirebaseAuthContainer.useContainer();
    const { showToast } = ToastContainer.useContainer();
    const [bots, setBots] = useState<ChatBot[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchString, setSearchString] = useState<string>("");
    const [selectedSort, setSelectedSort] = useState<string>("Name");
    const [createOpen, setCreateOpen] = useState<boolean>(false);
    const [editTarget, setEditTarget] = useState<ChatBot | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<ChatBot | null>(null);

    const companyId = user?.companyId;
    const kbUid = user?.currentKnowledgeBaseId;

    const reload = useCallback(async () => {
        if (!companyId || !kbUid) return;
        setIsLoading(true);
        try {
            const { data } = await fetchChatBots(companyId, kbUid);
            setBots(Array.isArray(data) ? data : (data?.items ?? []));
        } finally {
            setIsLoading(false);
        }
    }, [companyId, kbUid]);

    useEffect(() => {
        reload();
    }, [reload]);

    const filteredBots = useMemo(() => {
        const q = searchString.trim().toLowerCase();
        const list = q
            ? bots.filter(
                  b =>
                      b.name.toLowerCase().includes(q) ||
                      (platformLabel[b.platform] ?? b.platform).toLowerCase().includes(q) ||
                      b.cliqBotId?.toLowerCase().includes(q)
              )
            : bots;

        return [...list].sort((a, b) => {
            if (selectedSort === "Platform") {
                return (platformLabel[a.platform] ?? a.platform).localeCompare(
                    platformLabel[b.platform] ?? b.platform
                );
            }
            if (selectedSort === "Status") {
                return getStatusInfo(a).label.localeCompare(getStatusInfo(b).label);
            }
            return a.name.localeCompare(b.name);
        });
    }, [bots, searchString, selectedSort]);

    const handleCreate = async (req: CreateChatBotRequest) => {
        if (!companyId) return;
        await createChatBot(companyId, req);
        setCreateOpen(false);
        showToast("Chat bot created.", 3000);
        await reload();
    };

    const handleEdit = async (data: UpdateChatBotRequest) => {
        if (!companyId || !editTarget) return;
        await updateChatBot(companyId, editTarget.id, data);
        setEditTarget(null);
        showToast("Chat bot updated.", 3000);
        await reload();
    };

    const handleDelete = async () => {
        if (!companyId || !deleteTarget) return;
        try {
            await deleteChatBot(companyId, deleteTarget.id);
            showToast("Chat bot deleted.", 3000);
        } finally {
            setDeleteTarget(null);
            await reload();
        }
    };

    const handleCopy = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        showToast(`${label} copied.`, 2500);
    };

    const handleDownloadManifest = async (bot: ChatBot) => {
        if (!companyId) return;
        try {
            const { data } = await downloadManifest(companyId, bot.id);
            const url = window.URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${bot.name.replace(/[^a-zA-Z0-9-_]/g, "_")}-teams-manifest.zip`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch {
            showToast("Failed to download manifest.", 3000);
        }
    };

    const handleSearchChange = (evt: ChangeEvent<HTMLInputElement>) => {
        setSearchString(evt.target.value ?? "");
    };

    const clearSearchString = () => setSearchString("");

    const hasAssistant = !!kbUid;
    const showEmpty = hasAssistant && !isLoading && filteredBots.length === 0;

    return (
        <HeaderLayout blurHeader={false}>
            <div className="chat-integrations">
                <div className="chat-integrations-title">
                    <span className="chat-integrations-title__text" role="heading" aria-level={1}>
                        Chat Integrations
                    </span>
                    <div className="chat-integrations-title-search">
                        <SvgIcon
                            className="chat-integrations-title-search__icon"
                            component={SearchIcon}
                            inheritViewBox
                        />
                        <input
                            type="text"
                            aria-label="Search"
                            placeholder="Search"
                            onChange={handleSearchChange}
                            value={searchString}
                        />
                        {!!searchString && (
                            <IconButton
                                className="chat-integrations-title-search__button--clear"
                                onClick={clearSearchString}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </div>
                </div>
                <div className="chat-integrations-filters">
                    <div className="chat-integrations-filter chat-integrations-filter--sorting">
                        <SelectionFilter
                            list={sortOptions.map(o => `Sort by: ${o}`)}
                            selectedItem={`Sort by: ${selectedSort}`}
                            setSelectedItem={(val: string) =>
                                setSelectedSort(val.replace("Sort by: ", ""))
                            }
                            rightPosition={true}
                            minWidth={200}
                        />
                    </div>
                    <Button
                        className="chat-integrations-create-button"
                        variant="text"
                        startIcon={
                            <SvgIcon
                                className="chat-integrations-create-button__icon"
                                component={PlusIcon}
                                inheritViewBox
                            />
                        }
                        onClick={() => setCreateOpen(true)}
                        disabled={!hasAssistant}
                    >
                        Create Chat Bot
                    </Button>
                </div>
                <div className="chat-integrations-table-wrapper">
                    {!hasAssistant ? (
                        <div className="chat-integrations-empty">
                            Select an Assistant first to manage its chat integrations.
                        </div>
                    ) : showEmpty ? (
                        <div className="chat-integrations-empty">
                            {searchString
                                ? "No chat bots match your search."
                                : "No chat bots configured yet. Click 'Create Chat Bot' to add one."}
                        </div>
                    ) : (
                        <div
                            className="chat-integrations-table"
                            role="table"
                            aria-label="Chat integrations"
                        >
                            <div className="chat-integrations-table-header" role="row">
                                <div
                                    className="chat-integrations-table-cell chat-integrations-table-cell__name"
                                    role="columnheader"
                                >
                                    Name
                                </div>
                                <div
                                    className="chat-integrations-table-cell chat-integrations-table-cell__platform"
                                    role="columnheader"
                                >
                                    Platform
                                </div>
                                <div
                                    className="chat-integrations-table-cell chat-integrations-table-cell__id"
                                    role="columnheader"
                                >
                                    Bot ID
                                </div>
                                <div
                                    className="chat-integrations-table-cell chat-integrations-table-cell__status"
                                    role="columnheader"
                                >
                                    Status
                                </div>
                                <div
                                    className="chat-integrations-table-cell chat-integrations-table-cell__webhook"
                                    role="columnheader"
                                >
                                    Webhook URL
                                </div>
                                <div
                                    className="chat-integrations-table-cell chat-integrations-table-cell__action"
                                    role="columnheader"
                                >
                                    Actions
                                </div>
                            </div>
                            <div className="chat-integrations-table-body" role="rowgroup">
                                {filteredBots.map(bot => {
                                    const status = getStatusInfo(bot);
                                    return (
                                        <div
                                            key={bot.id}
                                            className="chat-integrations-table-row"
                                            role="row"
                                        >
                                            <div
                                                className="chat-integrations-table-cell chat-integrations-table-cell__name"
                                                role="rowheader"
                                            >
                                                <span className="chat-integrations-cell-text">
                                                    {bot.name}
                                                </span>
                                            </div>
                                            <div
                                                className="chat-integrations-table-cell chat-integrations-table-cell__platform"
                                                role="cell"
                                            >
                                                <span className="chat-integrations-cell-text">
                                                    {platformLabel[bot.platform] ?? bot.platform}
                                                </span>
                                            </div>
                                            <div
                                                className="chat-integrations-table-cell chat-integrations-table-cell__id"
                                                role="cell"
                                            >
                                                <div
                                                    className="chat-integrations-copyable"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Copy Bot ID for ${bot.name}`}
                                                    onClick={() =>
                                                        handleCopy(bot.cliqBotId, "Bot ID")
                                                    }
                                                    onKeyDown={activateOnKey}
                                                >
                                                    <span className="chat-integrations-copyable__text">
                                                        {bot.cliqBotId}
                                                    </span>
                                                    <SvgIcon
                                                        className="chat-integrations-copyable__icon"
                                                        component={CopyIcon}
                                                        inheritViewBox
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className="chat-integrations-table-cell chat-integrations-table-cell__status"
                                                role="cell"
                                            >
                                                <span
                                                    className={`chat-integrations-status chat-integrations-status--${status.color}`}
                                                >
                                                    {status.label}
                                                </span>
                                            </div>
                                            <div
                                                className="chat-integrations-table-cell chat-integrations-table-cell__webhook"
                                                role="cell"
                                            >
                                                <div
                                                    className="chat-integrations-copyable"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Copy Webhook URL for ${bot.name}`}
                                                    onClick={() =>
                                                        handleCopy(bot.webhookUrl, "Webhook URL")
                                                    }
                                                    onKeyDown={activateOnKey}
                                                >
                                                    <span className="chat-integrations-copyable__text">
                                                        {bot.webhookUrl}
                                                    </span>
                                                    <SvgIcon
                                                        className="chat-integrations-copyable__icon"
                                                        component={CopyIcon}
                                                        inheritViewBox
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className="chat-integrations-table-cell chat-integrations-table-cell__action"
                                                role="cell"
                                            >
                                                <Tooltip title="Edit Bot">
                                                    <span>
                                                        <IconButton
                                                            className="chat-integrations-action-button"
                                                            aria-label={`Edit ${bot.name}`}
                                                            onClick={() => setEditTarget(bot)}
                                                        >
                                                            <SvgIcon
                                                                component={EditIcon}
                                                                inheritViewBox
                                                            />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                {bot.platform === ChatPlatform.Teams && (
                                                    <Tooltip title="Download Teams Manifest">
                                                        <span>
                                                            <IconButton
                                                                className="chat-integrations-action-button"
                                                                aria-label={`Download Teams manifest for ${bot.name}`}
                                                                onClick={() =>
                                                                    handleDownloadManifest(bot)
                                                                }
                                                            >
                                                                <SvgIcon
                                                                    component={DownloadIcon}
                                                                    inheritViewBox
                                                                />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Delete Bot">
                                                    <span>
                                                        <IconButton
                                                            className="chat-integrations-action-button"
                                                            aria-label={`Delete ${bot.name}`}
                                                            onClick={() => setDeleteTarget(bot)}
                                                        >
                                                            <SvgIcon
                                                                component={DeleteIcon}
                                                                inheritViewBox
                                                            />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <LoaderView show={isLoading} />
                </div>
            </div>
            {createOpen && kbUid && (
                <CreateChatBotModal
                    open={createOpen}
                    knowledgeBaseUid={kbUid}
                    onCancel={() => setCreateOpen(false)}
                    onSubmit={handleCreate}
                />
            )}
            {editTarget && (
                <EditChatBotModal
                    open={!!editTarget}
                    bot={editTarget}
                    onCancel={() => setEditTarget(null)}
                    onSubmit={handleEdit}
                />
            )}
            <ConfirmationPopup
                open={!!deleteTarget}
                type={ConfirmationPopupType.dangerous}
                title="Delete Chat Bot?"
                description={`"${deleteTarget?.name ?? ""}" will be removed and its credentials deleted from Secrets Manager. This cannot be undone.`}
                actionName="Delete"
                showCancelButton
                handleAction={handleDelete}
                handleClose={() => setDeleteTarget(null)}
            />
            <Toast />
        </HeaderLayout>
    );
};

export default ChatIntegrationsView;
